/**
 * One rendered page, plus the layers that sit on top of it.
 *
 * The page itself is always the real PDF, rendered by pdf.js -- we never draw
 * our own approximation of the text. Above it sit three transparent layers:
 *
 *   - the text layer, which places a caret for editing existing content
 *   - the object layer, for things Odie has placed (draggable)
 *   - the paragraph layer, shown only in paragraph mode
 *
 * Every layer works in PDF user space (origin bottom-left, y upward) and
 * converts to CSS coordinates at the point of rendering, so there is exactly
 * one place where that flip happens per layer.
 */

import { useEffect, useRef, useState } from 'react'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { FormLayer } from './FormLayer'
import { MeasureLayer, type MeasureTool } from './MeasureLayer'
import { PrepareLayer } from './PrepareLayer'

import {
  api,
  type Comment,
  type CommentKind,
  type FindMatch,
  type FieldBuildKind,
  type FieldSuggestion,
  type FormField,
  type PageModel,
  type PageScale,
  type StructureBlock,
  type PdfLink,
  type Paragraph,
  type PlacedObject,
  type Run,
} from '../api'
import { ObjectLayer } from './ObjectLayer'
import { alongRun, unrotateDelta, unrotatePoint } from './rotation'
import type { Tool } from '../App'

/** Canvas pixels above which a page renders at reduced resolution. Chrome
 * refuses canvases much past 268 million pixels -- a 200-inch plan page at
 * 125% blanked the view -- and every canvas costs memory. */
const MAX_CANVAS_PIXELS = 16_000_000

/** How far outside the view a page is still rendered, so scrolling finds it drawn. */
const RENDER_MARGIN = '1200px 0px'

interface Props {
  doc: PDFDocumentProxy
  page: PageModel
  scale: number
  tool: Tool
  objects: PlacedObject[]
  sessionId: string | null
  /** The run being edited on this page, by runKey. */
  activeRun: string | null
  /** Start editing a run (its key), or stop (null); ``ending`` names the run
   * that is closing, so it cannot close one the user has moved on to. */
  setActiveRun: (key: string | null, ending?: string) => void
  selectedObject: string | null
  setSelectedObject: (id: string | null) => void
  onCommit: (run: Run, text: string) => void
  onProbe: (run: Run, text: string) => void
  onCanvasClick: (x: number, y: number) => void
  onObjectCommit: (id: string, patch: Record<string, number | undefined>) => void
  onObjectDelete: (id: string) => void
  onObjectEditText: (object: PlacedObject) => void
  onDeleteParagraph: (runIndices: number[]) => void
  onEditParagraph: (paragraph: Paragraph) => void
  /** A box drawn with the Whiteout tool, in PDF user space. */
  onWhiteoutArea: (rect: { x0: number; y0: number; x1: number; y1: number }) => void
  /** A box drawn for a certificate signature, in PDF user space. */
  onSignatureArea: (rect: { x0: number; y0: number; x1: number; y1: number }) => void
  /** The page's scales, for the live measuring readout. */
  scales: PageScale[]
  onMeasure: (tool: MeasureTool, points: number[]) => void
  /** Preparing a form: the field being edited, suggestions, and changes. */
  selectedField: string | null
  onSelectField: (name: string | null) => void
  onFieldGeometry: (name: string, widget: number, rect: [number, number, number, number]) => void
  onFieldArea: (kind: FieldBuildKind, rect: { x0: number; y0: number; x1: number; y1: number }) => void
  suggestions: FieldSuggestion[]
  onAcceptSuggestion: (index: number) => void
  onDismissSuggestion: (index: number) => void
  /** Reading order: this page's structure blocks, numbered in document order. */
  orderBlocks: { block: StructureBlock; number: number }[]
  selectedBlock: string | null
  onSelectBlock: (block: StructureBlock) => void
  onMoveRun: (runIndex: number, dx: number, dy: number) => void
  onRedactRect: (rect: { x0: number; y0: number; x1: number; y1: number }) => void
  onCropRect: (rect: { x0: number; y0: number; x1: number; y1: number }) => void
  /** A markup drawn on the page, in PDF user space. */
  onMarkup: (kind: CommentKind, geometry: MarkupGeometry) => void
  /** Find matches on this page; ``current`` is the one being looked at. */
  findMatches: FindMatch[]
  currentMatch: FindMatch | null
  comments: Comment[]
  activeComment: string | null
  onSelectComment: (id: string) => void
  /** Form fields with a widget on this page, and whether to tint them. */
  formFields: FormField[]
  highlightFields: boolean
  onFieldChange: (name: string, value: string | string[] | boolean | null) => void
  /** Links on this page. */
  links: PdfLink[]
  onFollowLink: (link: PdfLink) => void
  onDeleteLink: (id: string) => void
  /** An area drawn with the Link tool, in PDF user space. */
  onLinkArea: (rect: { x0: number; y0: number; x1: number; y1: number }) => void
  /** How much of the page is in view, 0-1, as it changes: the current page
   * follows scrolling. */
  onVisibility?: (pageIndex: number, ratio: number) => void
}

export interface MarkupGeometry {
  rect: { x0: number; y0: number; x1: number; y1: number }
  quads?: { x0: number; y0: number; x1: number; y1: number }[]
  points?: number[]
  ink?: number[][]
  arrow?: boolean
}

/** Tools that draw a markup by dragging on the page, and how. */
const DRAWN: Record<string, { kind: CommentKind | 'link' | 'sigbox' | 'field' | 'whiteout'; shape: 'box' | 'text' | 'segment' | 'path' }> = {
  link: { kind: 'link', shape: 'box' },
  'field-text': { kind: 'field', shape: 'box' },
  'field-checkbox': { kind: 'field', shape: 'box' },
  'field-radio': { kind: 'field', shape: 'box' },
  'field-combo': { kind: 'field', shape: 'box' },
  'field-list': { kind: 'field', shape: 'box' },
  'field-signature': { kind: 'field', shape: 'box' },
  sigbox: { kind: 'sigbox', shape: 'box' },
  whiteout: { kind: 'whiteout', shape: 'box' },
  highlight: { kind: 'highlight', shape: 'text' },
  underline: { kind: 'underline', shape: 'text' },
  strikeout: { kind: 'strikeout', shape: 'text' },
  rectangle: { kind: 'square', shape: 'box' },
  ellipse: { kind: 'circle', shape: 'box' },
  line: { kind: 'line', shape: 'segment' },
  arrow: { kind: 'line', shape: 'segment' },
  pen: { kind: 'ink', shape: 'path' },
}

type Box = { x0: number; y0: number; x1: number; y1: number }

/**
 * The spans of text a drag covers, one quad per line, clipped to the letters.
 *
 * Highlight, underline and strike-through snap to the text under the drag,
 * as in Acrobat, using the character positions the page model carries --
 * the PDF's own glyph positions, so the quads sit exactly on the letters.
 */
function textQuads(runs: Run[], area: Box): Box[] {
  const quads: Box[] = []
  for (const run of runs) {
    if (!run.visible || run.rotation || !run.charX?.length) continue
    const bottom = run.y - run.height * 0.22
    const top = run.y + run.height * 0.8
    if (top < area.y0 || bottom > area.y1) continue
    let first = -1
    let last = -1
    for (let i = 0; i < run.charX.length - 1; i++) {
      const left = run.x + run.charX[i]
      const right = run.x + run.charX[i + 1]
      if (right > area.x0 && left < area.x1 && run.text[i]?.trim()) {
        if (first < 0) first = i
        last = i
      }
    }
    if (first >= 0) {
      quads.push({ x0: run.x + run.charX[first], y0: bottom, x1: run.x + run.charX[last + 1], y1: top })
    }
  }
  return quads
}

function union(boxes: Box[]): Box {
  return {
    x0: Math.min(...boxes.map((b) => b.x0)), y0: Math.min(...boxes.map((b) => b.y0)),
    x1: Math.max(...boxes.map((b) => b.x1)), y1: Math.max(...boxes.map((b) => b.y1)),
  }
}

// Links over text follow on Ctrl+click; the page styles them clickable while
// Ctrl (or Cmd) is held.
if (typeof window !== 'undefined') {
  const track = (event: KeyboardEvent | MouseEvent) =>
    document.body.toggleAttribute('data-ctrl', event.ctrlKey || event.metaKey)
  window.addEventListener('keydown', track)
  window.addEventListener('keyup', track)
  window.addEventListener('mousemove', track, { passive: true })
  window.addEventListener('blur', () => document.body.removeAttribute('data-ctrl'))
}

/** A run's identity for the editor: its place on the page, which an edit
 * elsewhere on the page does not change (its instruction index does). */
export function runKey(run: Run): string {
  return `${Math.round(run.x * 10)}:${Math.round(run.y * 10)}:${run.fontSize}`
}

export function PageView(props: Props) {
  const { doc, page, scale, tool, objects, sessionId, activeComment } = props
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [paragraphs, setParagraphs] = useState<Paragraph[]>([])
  // Drag state for drawing a redaction box, in CSS pixels on this page.
  const [marquee, setMarquee] = useState<
    { x0: number; y0: number; x1: number; y1: number } | null
  >(null)
  // The pen's stroke in progress, in layer pixels.
  const [stroke, setStroke] = useState<{ x: number; y: number }[] | null>(null)
  const pageRef = useRef<HTMLDivElement>(null)
  // Only pages near the view are drawn: a 300-page document used to render
  // 300 full-resolution canvases at once, after every edit.
  const [near, setNear] = useState(false)
  const onVisibility = props.onVisibility

  useEffect(() => {
    const element = pageRef.current
    if (!element || typeof IntersectionObserver === 'undefined') {
      setNear(true)
      return
    }
    // Relative to the scrolling document view: against the window, the view's
    // own clipping hid the margin, so neighbouring pages drew only once seen.
    const root = element.closest('.canvas')
    const nearby = new IntersectionObserver(([entry]) => setNear(entry.isIntersecting),
      { root, rootMargin: RENDER_MARGIN })
    const seen = new IntersectionObserver(([entry]) => onVisibility?.(page.pageIndex, entry.intersectionRatio),
      { root, threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] })
    nearby.observe(element)
    seen.observe(element)
    return () => {
      nearby.disconnect()
      seen.disconnect()
    }
  }, [page.pageIndex, onVisibility])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!near) {
      // Far from view: give the pixels back.
      if (canvas && canvas.width) {
        canvas.width = 0
        canvas.height = 0
      }
      return
    }
    let cancelled = false
    let task: { cancel: () => void } | null = null

    void (async () => {
      const pdfPage = await doc.getPage(page.pageIndex + 1)
      if (cancelled) return
      if (!canvas) return

      // Render at device pixel ratio so text stays crisp on high-DPI screens,
      // but within the canvas budget: a huge page draws at lower resolution
      // rather than not at all.
      const dpr = window.devicePixelRatio || 1
      const shown = pdfPage.getViewport({ scale })
      const pixels = shown.width * shown.height * dpr * dpr
      const density = pixels > MAX_CANVAS_PIXELS ? dpr * Math.sqrt(MAX_CANVAS_PIXELS / pixels) : dpr
      const viewport = pdfPage.getViewport({ scale: scale * density })
      const context = canvas.getContext('2d')
      if (!context) return

      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)
      canvas.style.width = `${Math.floor(shown.width)}px`
      canvas.style.height = `${Math.floor(shown.height)}px`

      const render = pdfPage.render({ canvasContext: context, viewport })
      task = render
      try {
        await render.promise
      } catch (err) {
        if ((err as Error)?.name !== 'RenderingCancelledException') throw err
      }
    })()

    return () => {
      cancelled = true
      task?.cancel()
    }
  }, [doc, page.pageIndex, scale, near])

  // Paragraph outlines are only fetched when the mode is actually entered.
  useEffect(() => {
    if (tool !== 'paragraph' || !sessionId) {
      setParagraphs([])
      return
    }
    let cancelled = false
    void api
      .paragraphs(sessionId, page.pageIndex)
      .then((r) => !cancelled && setParagraphs(r.paragraphs))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [tool, sessionId, page.pageIndex])

  /** Convert a click on the page into absolute PDF user-space coordinates.
   *
   * The rendered box does not necessarily start at (0, 0): an offset MediaBox
   * or a crop moves its origin, and run coordinates are absolute. Adding the
   * origin back is what keeps clicks landing where the user aimed.
   */
  const toUserSpace = (event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const local = unrotate(event.clientX - rect.left, event.clientY - rect.top)
    return {
      x: page.originX + local.x / scale,
      y: page.originY + page.height - local.y / scale,
    }
  }

  // Tools placed by a single click. 'note' belongs here too: leaving it out
  // meant the Note tool armed but every click on the page was ignored.
  const placing =
    tool === 'text' ||
    tool === 'date' ||
    tool === 'image' ||
    tool === 'signature' ||
    tool === 'note' ||
    tool === 'stamp'

  // pdf.js rotates its own viewport, so the canvas already comes back turned.
  // The container has to match that, and the overlay layers -- which work in
  // unrotated page space -- get the same rotation applied once, as a group.
  const turned = page.rotation === 90 || page.rotation === 270
  const displayW = (turned ? page.height : page.width) * scale
  const displayH = (turned ? page.width : page.height) * scale
  const layerW = page.width * scale
  const layerH = page.height * scale

  const layerTransform = (() => {
    switch (page.rotation) {
      case 90:
        // rotate(90) maps (x,y) -> (-y,x); shift right by the new width.
        return `translate(${layerH}px, 0px) rotate(90deg)`
      case 180:
        return `translate(${layerW}px, ${layerH}px) rotate(180deg)`
      case 270:
        return `translate(0px, ${layerW}px) rotate(270deg)`
      default:
        return undefined
    }
  })()

  /** Undo the page rotation for a point in displayed CSS pixels. */
  const unrotate = (x: number, y: number) => {
    switch (page.rotation) {
      case 90:
        return { x: y, y: layerH - x }
      case 180:
        return { x: layerW - x, y: layerH - y }
      case 270:
        return { x: layerW - y, y: x }
      default:
        return { x, y }
    }
  }

  /** Convert a CSS-pixel marquee, in the layer's unrotated pixels (see
   * localPoint), into PDF user space. */
  const marqueeToUserSpace = (box: {
    x0: number
    y0: number
    x1: number
    y1: number
  }) => ({
    x0: page.originX + Math.min(box.x0, box.x1) / scale,
    x1: page.originX + Math.max(box.x0, box.x1) / scale,
    // Screen y grows downward; PDF y grows upward.
    y0: page.originY + page.height - Math.max(box.y0, box.y1) / scale,
    y1: page.originY + page.height - Math.min(box.y0, box.y1) / scale,
  })

  const overText = (link: PdfLink) => {
    const [x0, y0, x1, y1] = link.rect
    return page.runs.some((run) => run.visible && run.text.trim() &&
      run.x < x1 && run.x + run.width > x0 && run.y < y1 && run.y + run.height > y0)
  }

  /** A pointer position on a layer inside the rotated wrapper, in the
   * layer's own (unrotated) pixels. The bounding box is the rotated one, so
   * the offset has to be turned back -- without this, drawing a redaction
   * box on a rotated page destroyed a different area. */
  const localPoint = (event: React.PointerEvent) => {
    const rect = event.currentTarget.getBoundingClientRect()
    return unrotatePoint(page.rotation, event.clientX - rect.left, event.clientY - rect.top, layerW, layerH)
  }

  return (
    <div
      ref={pageRef}
      className="page"
      id={`page-${page.pageIndex}`}
      data-rotation={page.rotation || undefined}
      style={{ width: displayW, height: displayH }}
      onClick={(event) => {
        if (!placing) return
        const { x, y } = toUserSpace(event)
        props.onCanvasClick(x, y)
      }}
    >
      <canvas ref={canvasRef} className="page__canvas" />

      <div
        className="page__rotate"
        style={{
          width: layerW,
          height: layerH,
          transform: layerTransform,
          transformOrigin: '0 0',
        }}
      >
      {/* Existing document text -- editable in place. */}
      {tool === 'select' && (
        <div className="page__layer">
          {page.runs
            .filter((run) => run.visible && run.text.trim().length > 0)
            .map((run) => (
              <RunBox
                // Keyed by place, not instruction index: an edit elsewhere on
                // the page shifts the indices, and a box being typed in must
                // survive the reload that follows.
                key={runKey(run)}
                run={run}
                rotation={page.rotation}
                pageHeight={page.height}
                originX={page.originX}
                originY={page.originY}
                scale={scale}
                editing={props.activeRun === runKey(run)}
                onBeginEdit={() => run.editable && props.setActiveRun(runKey(run))}
                onEndEdit={() => props.setActiveRun(null, runKey(run))}
                onCommit={props.onCommit}
                onProbe={props.onProbe}
                onMove={props.onMoveRun}
              />
            ))}
        </div>
      )}

      {/* Odie-placed objects -- always draggable. */}
      {objects.length > 0 && (
        <ObjectLayer
          objects={objects}
          // While a placing tool is armed, a click on an object places on top
          // of it (a signature on a stamp) rather than grabbing it to move.
          passive={placing}
          rotation={page.rotation}
          pageHeight={page.height}
          originX={page.originX}
          originY={page.originY}
          scale={scale}
          selectedId={props.selectedObject}
          onSelect={props.setSelectedObject}
          onCommit={props.onObjectCommit}
          onEditText={props.onObjectEditText}
          onDelete={props.onObjectDelete}
        />
      )}

      {/* Links. Where one lies over text, a plain click still edits the text
          and Ctrl+click follows the link, as in Word; elsewhere (an image, a
          button drawn on the page) a click follows it. The Link tool shows
          them all, for removal. */}
      {props.links.length > 0 && (tool === 'select' || tool === 'link') && (
        <div className="page__layer link-layer" data-editing={tool === 'link' || undefined}>
          {props.links.map((link) => {
            const [x0, y0, x1, y1] = link.rect
            const where = link.uri ?? (link.targetPage !== null ? `Page ${link.targetPage + 1}` : 'Unsupported link')
            return (
              <a
                key={link.id}
                className="link"
                data-safe={link.safe || undefined}
                data-over-text={overText(link) || undefined}
                href={link.safe && link.uri ? link.uri : undefined}
                title={tool === 'link' ? where
                  : `${where}${!link.safe ? ' (not followed)' : overText(link) ? ' — Ctrl+click to follow' : ''}`}
                style={{
                  left: (x0 - page.originX) * scale,
                  top: (page.height - (y1 - page.originY)) * scale,
                  width: (x1 - x0) * scale,
                  height: (y1 - y0) * scale,
                }}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  if (tool === 'select' && link.safe) props.onFollowLink(link)
                }}
              >
                {tool === 'link' && (
                  <button
                    className="link__delete"
                    title="Remove this link"
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      props.onDeleteLink(link.id)
                    }}
                  >
                    {'✕'}
                  </button>
                )}
              </a>
            )
          })}
        </div>
      )}

      {/* Preparing a form: fields to select, move and resize; suggestions. */}
      {(tool === 'prepare' || tool.startsWith('field-')) && (
        <PrepareLayer
          fields={props.formFields}
          rotation={page.rotation}
          suggestions={props.suggestions}
          pageIndex={page.pageIndex}
          originX={page.originX}
          originY={page.originY}
          pageHeight={page.height}
          scale={scale}
          selected={props.selectedField}
          onSelect={props.onSelectField}
          onGeometry={props.onFieldGeometry}
          onAccept={props.onAcceptSuggestion}
          onDismiss={props.onDismissSuggestion}
        />
      )}

      {/* Measuring. */}
      {(tool === 'measure-distance' || tool === 'measure-perimeter' || tool === 'measure-area' || tool === 'calibrate') && (
        <MeasureLayer
          tool={tool as MeasureTool}
          rotation={page.rotation}
          width={layerW}
          height={layerH}
          scale={scale}
          originX={page.originX}
          originY={page.originY}
          pageHeight={page.height}
          scales={props.scales}
          onDone={(points) => props.onMeasure(tool as MeasureTool, points)}
        />
      )}

      {/* Reading order, numbered, while the accessibility panel is open. */}
      {props.orderBlocks.length > 0 && (
        <div className="page__layer order-layer" aria-hidden="true">
          {props.orderBlocks.filter(({ block }) => block.box).map(({ block, number }) => {
            const [x0, y0, x1, y1] = block.box!
            return (
              <div key={block.id} className="order-box" data-selected={props.selectedBlock === block.id || undefined}
                   data-type={block.type}
                   style={{
                     left: (x0 - page.originX) * scale, top: (page.height - (y1 - page.originY)) * scale,
                     width: (x1 - x0) * scale, height: (y1 - y0) * scale,
                   }}>
                <button className="order-box__num" onClick={(e) => { e.stopPropagation(); props.onSelectBlock(block) }}
                        title={`${number}. ${block.type}`}>{number}</button>
              </div>
            )
          })}
        </div>
      )}

      {/* Form fields, fillable in place. */}
      {tool === 'select' && props.formFields.length > 0 && (
        <FormLayer
          fields={props.formFields}
          pageIndex={page.pageIndex}
          originX={page.originX}
          originY={page.originY}
          pageHeight={page.height}
          scale={scale}
          highlight={props.highlightFields}
          onChange={props.onFieldChange}
        />
      )}

      {/* Drawn markup: highlight, underline, strike-through, shapes, lines, pen. */}
      {DRAWN[tool] && (() => {
        const spec = DRAWN[tool]
        const toUser = (x: number, y: number) => ({
          x: page.originX + x / scale,
          y: page.originY + page.height - y / scale,
        })
        const finish = () => {
          if (spec.shape === 'path') {
            const points = stroke ?? []
            setStroke(null)
            if (points.length < 2) return
            const ink = [points.flatMap((p) => { const u = toUser(p.x, p.y); return [u.x, u.y] })]
            const xs = ink[0].filter((_, i) => i % 2 === 0)
            const ys = ink[0].filter((_, i) => i % 2 === 1)
            props.onMarkup(spec.kind as CommentKind, {
              rect: { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) },
              ink,
            })
            return
          }
          if (!marquee) return
          const box = marquee
          setMarquee(null)
          const dx = Math.abs(box.x1 - box.x0)
          const dy = Math.abs(box.y1 - box.y0)
          if (spec.shape === 'segment') {
            if (Math.hypot(dx, dy) < 6) return
            const a = toUser(box.x0, box.y0)
            const b = toUser(box.x1, box.y1)
            props.onMarkup(spec.kind as CommentKind, {
              rect: marqueeToUserSpace(box), points: [a.x, a.y, b.x, b.y], arrow: tool === 'arrow',
            })
            return
          }
          if (dx < 6 || dy < 4) return
          const area = marqueeToUserSpace(box)
          if (spec.kind === 'link') {
            props.onLinkArea(area)
            return
          }
          if (spec.kind === 'sigbox') {
            props.onSignatureArea(area)
            return
          }
          if (spec.kind === 'whiteout') {
            props.onWhiteoutArea(area)
            return
          }
          if (spec.kind === 'field') {
            props.onFieldArea(tool.slice(6) as FieldBuildKind, area)
            return
          }
          if (spec.shape === 'text') {
            const quads = textQuads(page.runs, area)
            props.onMarkup(spec.kind as CommentKind, quads.length ? { rect: union(quads), quads } : { rect: area, quads: [area] })
            return
          }
          props.onMarkup(spec.kind as CommentKind, { rect: area })
        }
        return (
          <div
            className="redact-layer draw-layer"
            data-tool={tool}
            onPointerDown={(event) => {
              event.preventDefault()
              ;(event.target as Element).setPointerCapture?.(event.pointerId)
              const { x, y } = localPoint(event)
              if (spec.shape === 'path') setStroke([{ x, y }])
              else setMarquee({ x0: x, y0: y, x1: x, y1: y })
            }}
            onPointerMove={(event) => {
              const { x, y } = localPoint(event)
              if (stroke) setStroke([...stroke, { x, y }])
              else if (marquee) setMarquee({ ...marquee, x1: x, y1: y })
            }}
            onPointerUp={finish}
            onPointerCancel={() => { setMarquee(null); setStroke(null) }}
          >
            {marquee && spec.shape !== 'segment' && (
              <div
                className={spec.shape === 'text' ? 'highlight-marquee' : 'shape-marquee'}
                data-round={tool === 'ellipse' || undefined}
                style={{
                  left: Math.min(marquee.x0, marquee.x1),
                  top: Math.min(marquee.y0, marquee.y1),
                  width: Math.abs(marquee.x1 - marquee.x0),
                  height: Math.abs(marquee.y1 - marquee.y0),
                }}
              />
            )}
            {(stroke || (marquee && spec.shape === 'segment')) && (
              <svg className="draw-preview" width={layerW} height={layerH}>
                {stroke && <polyline points={stroke.map((p) => `${p.x},${p.y}`).join(' ')} />}
                {marquee && spec.shape === 'segment' && (
                  <line x1={marquee.x0} y1={marquee.y0} x2={marquee.x1} y2={marquee.y1} />
                )}
              </svg>
            )}
          </div>
        )
      })()}

      {/* Find matches on this page; the current one stands out. */}
      {props.findMatches.length > 0 && (
        <div className="page__layer find-layer" aria-hidden="true">
          {props.findMatches.map((match) => {
            const [x0, y0, x1, y1] = match.rect
            const current = props.currentMatch === match
            return (
              <div
                key={`${match.segmentId}:${match.start}`}
                className="find-hit"
                data-current={current || undefined}
                id={current ? 'find-current' : undefined}
                style={{
                  left: (x0 - page.originX) * scale,
                  top: (page.height - (y1 - page.originY)) * scale,
                  width: (x1 - x0) * scale,
                  height: (y1 - y0) * scale,
                }}
              />
            )
          })}
        </div>
      )}

      {/* Comment markers. Replies live in the sidebar, not on the page. */}
      {props.comments.length > 0 && (
        <div className="page__layer comment-layer">
          {props.comments
            .filter((c) => c.pageIndex === page.pageIndex && !c.inReplyTo)
            .map((comment) => {
              const [x0, y0, x1, y1] = comment.rect
              const fill = `rgb(${comment.color
                .map((c) => Math.round(c * 255))
                .join(',')})`
              return (
                <button
                  key={comment.id}
                  className="marker"
                  data-kind={comment.kind}
                  data-active={comment.id === activeComment}
                  title={
                    (comment.author ? comment.author + ': ' : '') +
                    (comment.contents || '(no text)')
                  }
                  style={{
                    left: (x0 - page.originX) * scale,
                    top: (page.height - (y1 - page.originY)) * scale,
                    width: Math.max((x1 - x0) * scale, 12),
                    height: Math.max((y1 - y0) * scale, 12),
                    // Everything but a sticky note draws itself in the PDF --
                    // a stamp's picture, a text box, a shape -- so its marker
                    // only needs to be clickable and stays transparent. Filled,
                    // it hid a stamped signature behind a yellow box.
                    background:
                      comment.kind === 'note' ? fill : 'transparent',
                    borderColor: fill,
                  }}
                  onClick={(event) => {
                    event.stopPropagation()
                    props.onSelectComment(comment.id)
                  }}
                />
              )
            })}
        </div>
      )}

      {/* Draw-to-crop. Same marquee mechanics as redaction. */}
      {tool === 'crop' && (
        <div
          className="redact-layer crop-layer"
          onPointerDown={(event) => {
            event.preventDefault()
            const { x, y } = localPoint(event)
            setMarquee({ x0: x, y0: y, x1: x, y1: y })
          }}
          onPointerMove={(event) => {
            if (!marquee) return
            const { x, y } = localPoint(event)
            setMarquee({ ...marquee, x1: x, y1: y })
          }}
          onPointerUp={() => {
            if (!marquee) return
            const box = marquee
            setMarquee(null)
            if (Math.abs(box.x1 - box.x0) < 8 || Math.abs(box.y1 - box.y0) < 8) {
              return
            }
            props.onCropRect(marqueeToUserSpace(box))
          }}
          onPointerLeave={() => setMarquee(null)}
        >
          {marquee && (
            <div
              className="crop-marquee"
              style={{
                left: Math.min(marquee.x0, marquee.x1),
                top: Math.min(marquee.y0, marquee.y1),
                width: Math.abs(marquee.x1 - marquee.x0),
                height: Math.abs(marquee.y1 - marquee.y0),
              }}
            />
          )}
        </div>
      )}

      {/* Draw-to-redact. Drawn last so it sits above the other layers. */}
      {tool === 'redact' && (
        <div
          className="redact-layer"
          onPointerDown={(event) => {
            event.preventDefault()
            const { x, y } = localPoint(event)
            setMarquee({ x0: x, y0: y, x1: x, y1: y })
          }}
          onPointerMove={(event) => {
            if (!marquee) return
            const { x, y } = localPoint(event)
            setMarquee({ ...marquee, x1: x, y1: y })
          }}
          onPointerUp={() => {
            if (!marquee) return
            const box = marquee
            setMarquee(null)
            // Ignore stray clicks; a real selection has area.
            if (Math.abs(box.x1 - box.x0) < 4 || Math.abs(box.y1 - box.y0) < 4) {
              return
            }
            props.onRedactRect(marqueeToUserSpace(box))
          }}
          onPointerLeave={() => setMarquee(null)}
        >
          {marquee && (
            <div
              className="redact-marquee"
              style={{
                left: Math.min(marquee.x0, marquee.x1),
                top: Math.min(marquee.y0, marquee.y1),
                width: Math.abs(marquee.x1 - marquee.x0),
                height: Math.abs(marquee.y1 - marquee.y0),
              }}
            />
          )}
        </div>
      )}

      {/* Paragraph selection. */}
      {tool === 'paragraph' && (
        <div className="page__layer">
          {paragraphs.map((paragraph, index) => (
            <div
              key={index}
              className="para"
              style={{
                left: (paragraph.x - page.originX) * scale,
                top: (page.height - (paragraph.top - page.originY)) * scale,
                width: Math.max((paragraph.right - paragraph.x) * scale, 20),
                height: Math.max((paragraph.top - paragraph.y) * scale, 12),
              }}
              title={paragraph.text.slice(0, 200)}
              onDoubleClick={(event) => {
                event.stopPropagation()
                props.onEditParagraph(paragraph)
              }}
            >
              <span className="para__badges">
                <button className="para__badge" title="Edit this paragraph as text; its lines re-flow"
                        onClick={(event) => { event.stopPropagation(); props.onEditParagraph(paragraph) }}>
                  Edit {'\u00B6'}
                </button>
                <button className="para__badge para__badge--danger"
                        title={`Delete this paragraph (${paragraph.runIndices.length} runs)`}
                        onClick={(event) => { event.stopPropagation(); props.onDeleteParagraph(paragraph.runIndices) }}>
                  Delete {'\u00B6'}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  )
}

interface RunBoxProps {
  run: Run
  /** The page's /Rotate: the box is drawn turned, the pointer is not. */
  rotation: number
  pageHeight: number
  originX: number
  originY: number
  scale: number
  editing: boolean
  onBeginEdit: () => void
  onEndEdit: () => void
  onCommit: (run: Run, text: string) => void
  onProbe: (run: Run, text: string) => void
  onMove: (runIndex: number, dx: number, dy: number) => void
}

function RunBox({
  run,
  rotation,
  pageHeight,
  originX,
  originY,
  scale,
  editing,
  onBeginEdit,
  onEndEdit,
  onCommit,
  onProbe,
  onMove,
}: RunBoxProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState(run.text)
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null)
  const [offset, setOffset] = useState<{ x: number; y: number } | null>(null)
  /** Where a click asked for the caret, as a character offset. */
  const pendingCaret = useRef<number | null>(null)

  // A click puts the caret where it landed, as in Word; double-click then
  // selects the word, natively. Arriving by keyboard (Tab) selects the whole
  // run instead, so typing replaces it.
  useEffect(() => {
    if (!editing) return
    const element = ref.current
    if (!element) return

    setDraft(run.text)
    // A refused edit left its text in the box; start from the document's.
    if (element.textContent !== run.text) element.textContent = run.text
    element.focus()

    const caret = pendingCaret.current
    pendingCaret.current = null
    const range = document.createRange()
    const node = element.firstChild
    if (caret !== null && node && node.nodeType === Node.TEXT_NODE) {
      range.setStart(node, Math.min(caret, node.textContent?.length ?? 0))
      range.collapse(true)
    } else {
      range.selectNodeContents(element)
    }
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
  }, [editing, run.text])

  /**
   * The character boundary nearest a click, from the PDF's own glyph
   * positions. The box shows the text in the interface font, whose widths
   * differ from the document's, so the browser's own hit-testing would land
   * the caret letters away on a long line.
   */
  const caretAt = (clientX: number, clientY: number): number | null => {
    const element = ref.current
    if (!element || run.rotation || !run.charX?.length) return null
    const local = alongRun(rotation, element.getBoundingClientRect(), clientX, clientY) / scale
    let best = 0
    for (let i = 1; i < run.charX.length; i++) {
      if (Math.abs(run.charX[i] - local) < Math.abs(run.charX[best] - local)) best = i
    }
    return best
  }

  const commit = () => {
    onEndEdit()
    // A run is one line: pasted tabs and line breaks become spaces, and a
    // decomposed accent (as macOS pastes it) is composed, so the font's own
    // letter is used.
    const text = draft.replace(/[\t\r\n\u2028\u2029]+/g, ' ').normalize('NFC')
    if (text !== run.text) onCommit(run, text)
  }

  // Run coordinates are absolute user space; subtract the box origin.
  const left = (run.x - originX) * scale + (offset?.x ?? 0)
  const top =
    (pageHeight - (run.y - originY) - run.fontSize * 0.8) * scale +
    (offset?.y ?? 0)

  return (
    <div
      ref={ref}
      className="run"
      data-editing={editing}
      data-locked={!run.editable}
      contentEditable={editing}
      suppressContentEditableWarning
      role="textbox"
      tabIndex={0}
      aria-label={run.text}
      title={
        run.editable
          ? `${run.fontFamily} ${run.fontSize.toFixed(1)}pt — click to edit, Alt+drag to move`
          : 'This run’s font could not be read, so it is not editable.'
      }
      style={{
        left,
        top,
        minWidth: run.width * scale,
        height: Math.max(run.fontSize * scale * 1.2, 10),
        fontSize: run.fontSize * scale,
        lineHeight: 1.2,
        transform: run.rotation ? `rotate(${-run.rotation}deg)` : undefined,
      }}
      // Alt+drag repositions existing document text without entering the editor.
      // The first click is Odie's, not the browser's: left to itself, the
      // browser would finish its own click-selection after Odie placed the
      // caret, measuring with the interface font and landing a letter or
      // more off. Once editing, clicks behave natively again.
      onMouseDown={(event) => {
        if (editing || event.altKey || !run.editable) return
        event.preventDefault()
        pendingCaret.current = caretAt(event.clientX, event.clientY)
      }}
      onPointerDown={(event) => {
        if (!event.altKey || !run.editable) return
        event.preventDefault()
        ;(event.target as Element).setPointerCapture?.(event.pointerId)
        drag.current = { x: event.clientX, y: event.clientY, moved: false }
      }}
      onPointerMove={(event) => {
        if (!drag.current) return
        drag.current.moved = true
        // In the page's own axes, so the text follows the pointer on a
        // rotated page too.
        setOffset(unrotateDelta(rotation, event.clientX - drag.current.x, event.clientY - drag.current.y))
      }}
      onPointerUp={() => {
        const state = drag.current
        drag.current = null
        if (!state?.moved || !offset) {
          setOffset(null)
          return
        }
        setOffset(null)
        onMove(run.index, offset.x / scale, -offset.y / scale)
      }}
      onClick={(event) => {
        if (event.altKey) return
        onBeginEdit()
      }}
      onFocus={onBeginEdit}
      onInput={(event) => {
        const text = event.currentTarget.textContent ?? ''
        setDraft(text)
        onProbe(run, text)
      }}
      onBlur={() => editing && commit()}
      onKeyDown={(event) => {
        // A content-stream run cannot contain a newline, so Enter commits
        // and Shift+Enter does nothing (it inserted a break no font encodes).
        if (event.key === 'Enter') {
          event.preventDefault()
          if (!event.shiftKey) commit()
        } else if (event.key === 'Escape') {
          event.preventDefault()
          if (ref.current) ref.current.textContent = run.text
          setDraft(run.text)
          onEndEdit()
        }
      }}
    >
      {run.text}
    </div>
  )
}
